'use client';

import React from 'react';
import { EventCard } from './EventCard';
import SkeletonCard from '@/components/shared/SkeletonCard';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Ticket, ExternalLink } from 'lucide-react';
import { Event } from '@/lib/hooks/useEvents';

interface EventsSectionProps {
	events: Event[];
	loading: boolean;
	error: string | null;
	isStandalone: boolean;
	title: string;
	emptyMessage: string;
	icon: React.ComponentType<{ className?: string }>;
}

function EventsSection({
	events,
	loading,
	error,
	isStandalone,
	title,
	emptyMessage,
	icon: Icon,
}: EventsSectionProps) {
	if (error) {
		return (
			<div className='text-center text-red-500 py-8'>
				<p>Error loading events: {error}</p>
			</div>
		);
	}

	if (loading) {
		return (
			<div className='space-y-4'>
				<div className='flex items-center gap-2 mb-4'>
					{/* <Icon className='w-5 h-5 text-primary' /> */}
					<h2 className='text-xl font-semibold text-foreground'>{title}</h2>
				</div>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
					{[...Array(6)].map((_, index) => (
						<SkeletonCard key={index} />
					))}
				</div>
			</div>
		);
	}

	if (events.length === 0) {
		return (
			<div className='space-y-4'>
				<div className='flex items-center gap-2 mb-4'>
					{/* <Icon className='w-5 h-5 text-primary' /> */}
					<h2 className='text-xl font-semibold text-foreground'>{title}</h2>
				</div>
				<div className='text-center py-12 text-muted-foreground'>
					{/* <Icon className='w-12 h-12 mx-auto mb-4 opacity-50' /> */}
					<p className='text-lg'>{emptyMessage}</p>
				</div>
			</div>
		);
	}

	return (
		<div className='space-y-4'>
			<div className='flex items-center justify-between mb-4'>
				<div className='flex items-center gap-2'>
					{/* <Icon className='w-5 h-5 text-primary' /> */}
					<h2 className='text-xl font-semibold text-foreground'>{title}</h2>
					<Badge variant='secondary' className='ml-2'>
						{events.length} event{events.length !== 1 ? 's' : ''}
					</Badge>
				</div>
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
				{events.map((event) => (
					<EventCard key={event.id} event={event} isStandalone={isStandalone} />
				))}
			</div>
		</div>
	);
}

interface SeparatedEventsProps {
	events: Event[];
	loading: boolean;
	error: string | null;
	isStandalone: boolean;
}

export function SeparatedEvents({
	events,
	loading,
	error,
	isStandalone,
}: SeparatedEventsProps) {
	const internalEvents = events.filter((event) => !event.isExternal);
	const externalEvents = events.filter((event) => event.isExternal);

	return (
		<div className='space-y-8'>
			{/* Internal Events Section */}
			<EventsSection
				events={internalEvents}
				loading={loading}
				error={error}
				isStandalone={isStandalone}
				title='ThrughPass'
				emptyMessage='No ThrughPass events found for this time period'
				icon={Ticket}
			/>

			{/* Separator */}
			{internalEvents.length > 0 && externalEvents.length > 0 && (
				<div className='relative'>
					<Separator className='my-8' />
					<div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background px-4'>
						<Badge variant='outline' className='text-muted-foreground'>
							Also Check Out
						</Badge>
					</div>
				</div>
			)}

			{/* External Events Section */}
			<EventsSection
				events={externalEvents}
				loading={false} // Don't show loading for external events if internal events are loaded
				error={null}
				isStandalone={isStandalone}
				title='Travel Guide Events'
				emptyMessage='No Travel Guide events found for this time period'
				icon={ExternalLink}
			/>
		</div>
	);
}
